import styled, { css } from 'styled-components';

import { Link, Icon } from '@powdr/components';
import {
  Components, ThemeProps as Prop, Breakpoints, Alignment,
  FontFamily,
  TextTransformOptions,
} from '@powdr/constants';
import {
  componentColor, getFontConfigValues, getFontStyles, themeUtil,
} from '@powdr/utils';

const CPNT = Components.UI_TABLE;

export const StyledUiTable = styled.div`
  ${Breakpoints.smaller(css`
    overflow-x: auto;
  `)}

  h1, h2, h3, h4, h5, h6 {
    color: ${({ colorProfile }) => themeUtil(Prop.HEADER, CPNT, colorProfile)};
  }
`;

export const TableWrapper = styled.div`
  display: flex;

  ${Breakpoints.mobile(css`
    ${({ $isHorizontalScroll }) => (($isHorizontalScroll) && 'margin-bottom: 5px;')}
  `)}
`;

export const Table = styled.table`
  table-layout: fixed;
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;
  margin-bottom: 40px;

  ${Breakpoints.mobile(css`
    ${({ $isHorizontalScroll }) => (($isHorizontalScroll) && 'min-width: 700px; margin-bottom: 20px;')}
  `)}
`;

export const TableBody = styled.tbody`
  display: table;
  width: 100%;

  a:not(.cell-button) {
    transition: color .25s ease-out, background-color .25s ease-out;
    color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, colorProfile)};

    &:hover, &:focus {
      transition: color .25s ease-out, background-color .25s ease-out;
      color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, colorProfile)};
    }

    &:focus {
      outline: 2px solid ${({ colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, colorProfile)};
      outline-offset: 2px;
    }
  }
`;

export const Row = styled.tr`
  display: table-row;
  border-bottom: 1px solid ${({ colorProfile }) => themeUtil(Prop.BORDER, CPNT, colorProfile)};
`;

export const HeaderCell = styled.th`
  display: ${({ $isHideHeaderCell }) => (($isHideHeaderCell) ? 'none' : 'table-cell')};
  padding: 10px 15px 10px 15px;
  width: auto;
  line-height: 1.2em;
  text-transform: uppercase;
  color: ${({ colorProfile }) => themeUtil(Prop.CONTENT, CPNT, colorProfile)};
  background-color: ${({ colorProfile }) => componentColor(Prop.HEADER_BG, CPNT, Prop.BACKGROUND, colorProfile)};
  text-align: center;
  ${() => getFontStyles(
    FontFamily.PRIMARY_FONT,
    getFontConfigValues(FontFamily.BASE_FONT, CPNT, 'data-cell').defaultSize, // setup so that font size by default will follow base font if there is no config to override it
    undefined,
    undefined,
    CPNT,
    'data-header',
  )};

  ${Breakpoints.mobile(css`
    ${() => getFontStyles(
    FontFamily.PRIMARY_FONT,
    getFontConfigValues(FontFamily.BASE_FONT, CPNT, 'data-cell').defaultSize, // setup so that font size by default will follow base font if there is no config to override it
    0.9,
    undefined,
    CPNT,
    'data-header',
  )};
  `)}

  ${Breakpoints.larger(css`
    ${() => getFontStyles(
    FontFamily.PRIMARY_FONT,
    getFontConfigValues(FontFamily.BASE_FONT, CPNT, 'data-cell').defaultSize, // setup so that font size by default will follow base font if there is no config to override it
    1.1,
    undefined,
    CPNT,
    'data-header',
  )};
  `)}

  &:first-child {
    text-align: ${({ isFirstColumnCenter }) => ((isFirstColumnCenter) ? 'center' : 'left')};
  }

  &:last-child {
    text-align: ${({ isLastColumnCenter }) => ((isLastColumnCenter) ? 'center' : 'right')};
  }

  ${({ isBoldCell }) => ((isBoldCell) && 'font-weight: bold;')}
`;

export const DataCell = styled.td`
  display: table-cell;
  padding: 10px 15px 10px 15px;
  width: auto;
  line-height: 1.2em;
  color: ${({ colorProfile }) => themeUtil(Prop.CONTENT, CPNT, colorProfile)};
  text-align: center;

  ${() => getFontStyles(FontFamily.BASE_FONT, undefined, undefined, undefined, CPNT, 'data-cell')};

  ${Breakpoints.mobile(css`
    ${() => getFontStyles(FontFamily.BASE_FONT, undefined, 0.9, undefined, CPNT, 'data-cell')};
  `)}

  ${Breakpoints.larger(css`
    ${() => getFontStyles(FontFamily.BASE_FONT, undefined, 1.1, undefined, CPNT, 'data-cell')};
  `)}

  ${({ isBoldCell }) => ((isBoldCell) ? 'font-weight: bold;' : '')}

  &:first-child {
    text-align: ${({ isFirstColumnCenter }) => ((isFirstColumnCenter) ? 'center' : 'left')};
  }

  &:last-child {
    text-align: ${({ isLastColumnCenter }) => ((isLastColumnCenter) ? 'center' : 'right')};
  }

  ${({ isFirstColumnBold }) => ((isFirstColumnBold) && `
      &:first-child {
        ${() => getFontStyles(FontFamily.PRIMARY_FONT, undefined, undefined, TextTransformOptions.UPPERCASE, CPNT, 'data-cell')};
      }`
  )}
`;

export const CellLink = styled(Link)`
  display: inline-block;
  padding: 10px 15px;
  text-transform: uppercase;
  ${() => getFontStyles(FontFamily.SECONDARY_FONT, undefined, undefined, undefined)};
  color: ${({ colorProfile }) => themeUtil(Prop.BTN_TXT, CPNT, colorProfile)};
  background-color: ${({ colorProfile }) => themeUtil(Prop.BTN_BG, CPNT, colorProfile)};
  transition: color .25s ease-out, background-color .25s ease-out;

  &:hover, &:focus {
    transition: color .25s ease-out, background-color .25s ease-out;
    color: ${({ colorProfile }) => themeUtil(Prop.BTN_TXT_HOVER, CPNT, colorProfile)};
    background-color: ${({ colorProfile }) => themeUtil(Prop.BTN_BG_HOVER, CPNT, colorProfile)};
  }

  &:focus {
    outline: 2px solid ${({ colorProfile }) => themeUtil(Prop.BTN_BG_HOVER, CPNT, colorProfile)};;
    outline-offset: 2px;
  }

  ${Breakpoints.mobile(css`
    margin: 0 1px;
  `)}
`;

export const CellIcon = styled(Icon)`
  margin: 0 auto;

  svg {
    fill: ${({ color, colorProfile }) => ((!color) ? themeUtil(Prop.ICON, CPNT, colorProfile) : '')};
    color: ${({ color, colorProfile }) => ((!color) ? themeUtil(Prop.ICON, CPNT, colorProfile) : '')};
  }
`;

export const MobileScrollBlur = styled.div`
  position: absolute;
  overflow-y: hidden;
  ${({ side }) => ((side === Alignment.LEFT) ? 'left: 0;' : 'right: 0; transform: rotate(180deg);')}
`;

export const MobileScrollInViewHelper = styled.div`
  height: 100px;
`;

export const BlurIcon = styled(Icon)`
  max-height: ${({ maxHeight }) => `${maxHeight}px`};
`;
